import { Suspense, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ApiFehler } from '../api/client';
import { useIch } from '../api/abfragen';
import { useMeinTagAn } from '../api/meintag';
import { useAuth } from '../lib/auth';
import { Hinweis, Knopf, Laedt, NoraKnopf, TabLeiste, Wortmarke } from '../ui';
import { OfflineHinweis } from './OfflineHinweis';
import s from './layout.module.css';

/** Schuetzt alles darunter: ohne Sitzung zur Anmeldung, ohne Familie ein klarer Hinweis. */
export function Geschuetzt({ children }: { children: ReactNode }) {
  const { zustand, abmelden } = useAuth();
  const ort = useLocation();
  const ich = useIch();

  if (zustand.status === 'laedt') return <Laedt />;
  if (zustand.status === 'abgemeldet') {
    return <Navigate to="/anmelden" replace state={zustand.bewusst ? undefined : { von: ort.pathname }} />;
  }

  if (ich.isError) {
    const keinZugang = ich.error instanceof ApiFehler && ich.error.status === 403;
    return (
      <main className={s.seiteOhneTabs}>
        <Wortmarke alsLink={false} />
        <div className={s.abschnitt}>
          <Hinweis art={keinZugang ? 'warnung' : 'fehler'} titel={keinZugang ? 'Noch kein Zugang' : 'Das hat nicht geklappt'}>
            {keinZugang
              ? 'Dein Konto gehört noch zu keiner Familie. Bitte jemanden aus deiner Familie, dich einzuladen.'
              : 'Nebenbei ist gerade nicht erreichbar. Versuch es gleich noch mal.'}
          </Hinweis>
        </div>
        <div className={s.abschnitt}>
          <Knopf art="kontur" icon="abmelden" onClick={() => void abmelden()}>
            Abmelden
          </Knopf>
        </div>
      </main>
    );
  }
  if (!ich.data) return <Laedt />;
  return <>{children}</>;
}

/** App-Huelle mit den fuenf Tabs und dem schwebenden Nora-Knopf. */
export function AppHuelle() {
  const ort = useLocation();
  // Lyra P1: auf "Was Nora ueber euch weiss" kein schwebender Nora-Knopf ("Nora etwas Neues sagen" tut dasselbe)
  // QA Runde 2 N13: in Einkaufen auch nicht; dort verdeckte er die Mengenspalte, und im Laden braucht es Nora nicht
  const ohneNoraKnopf = ort.pathname === '/profil/nora' || ort.pathname === '/einkaufen';
  const meinTag = useMeinTagAn();
  return (
    <Geschuetzt>
      <OfflineHinweis />
      <main className={s.seite} data-huelle="">
        {/* Seiten-Chunks laden innerhalb der Huelle: Tab-Leiste und Nora-Knopf bleiben stehen */}
        <Suspense fallback={<Laedt />}>
          <Outlet />
        </Suspense>
      </main>
      {!ohneNoraKnopf && <NoraKnopf />}
      <TabLeiste meinTag={meinTag} />
    </Geschuetzt>
  );
}
